'use client';

import { memo, useCallback, useMemo, useState } from 'react';
import Image, { type ImageProps } from 'next/image';

type AppImageProps = Omit<ImageProps, 'src' | 'alt' | 'onError' | 'onLoad'> & {
  src: string;
  alt: string;
  fallbackSrc?: string;
};

const AppImage = memo(function AppImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  quality = 85,
  placeholder = 'empty',
  blurDataURL,
  fill = false,
  sizes,
  onClick,
  fallbackSrc = '/assets/images/no_image.png',
  loading = 'lazy',
  unoptimized = false,
  ...props
}: AppImageProps) {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const isExternalUrl = useMemo(() => imageSrc.startsWith('http'), [imageSrc]);
  const resolvedUnoptimized = unoptimized || isExternalUrl;

  const handleError = useCallback(() => {
    if (!hasError && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(true);
    }
    setIsLoading(false);
  }, [fallbackSrc, hasError, imageSrc]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const imageClassName = useMemo(() => {
    const classes = [className];
    if (isLoading) classes.push('bg-gray-200');
    if (onClick) classes.push('cursor-pointer hover:opacity-90 transition-opacity duration-200');
    return classes.filter(Boolean).join(' ');
  }, [className, isLoading, onClick]);

  const sharedProps = {
    ...props,
    className: imageClassName,
    quality,
    placeholder,
    unoptimized: resolvedUnoptimized,
    priority,
    loading: priority ? undefined : loading,
    onError: handleError,
    onLoad: handleLoad,
    onClick,
    ...(blurDataURL && placeholder === 'blur' ? { blurDataURL } : {}),
  } satisfies Omit<ImageProps, 'src' | 'alt' | 'width' | 'height' | 'fill' | 'sizes'>;

  if (fill) {
    return (
      <div className="relative h-full w-full">
        <Image
          src={imageSrc}
          alt={alt}
          {...sharedProps}
          fill
          sizes={sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'}
          style={{ objectFit: 'cover', ...props.style }}
        />
      </div>
    );
  }

  return <Image src={imageSrc} alt={alt} {...sharedProps} width={width || 400} height={height || 300} sizes={sizes}/>;
});

AppImage.displayName = 'AppImage';

export default AppImage;
